/**
 * Tags the note editor can produce, and the only ones rendered back. Anything
 * else in a stored note is unwrapped to its text, so a note written by another
 * client — or pasted in from anywhere — cannot bring markup of its own.
 */
const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'BR', 'P', 'DIV', 'SPAN', 'UL', 'OL', 'LI', 'A',
])

/** Attributes kept per tag. Everything not listed here is dropped. */
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  A: new Set(['href', 'title']),
}

/**
 * What published content such as a release note may carry on top of a note's
 * tags: headings, images and code. Written by Adnuntius admins in an HTML
 * editor rather than by users in the note editor.
 */
const RICH_TAGS = new Set([
  ...ALLOWED_TAGS, 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'IMG', 'HR', 'BLOCKQUOTE', 'CODE', 'PRE',
])

const RICH_ATTRS: Record<string, Set<string>> = {
  ...ALLOWED_ATTRS,
  IMG: new Set(['src', 'alt', 'width', 'height']),
}

const SAFE_HREF = /^(https?:|mailto:|tel:)/i
const SAFE_SRC = /^https?:/i

interface Allowlist {
  tags: Set<string>
  attrs: Record<string, Set<string>>
}

const NOTE_ALLOWLIST: Allowlist = { tags: ALLOWED_TAGS, attrs: ALLOWED_ATTRS }
const RICH_ALLOWLIST: Allowlist = { tags: RICH_TAGS, attrs: RICH_ATTRS }

/** Tags removed with their content, rather than unwrapped to text. */
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META'])

function cleanChildren(parent: Element, allowlist: Allowlist) {
  for (const child of Array.from(parent.children)) cleanNode(child, allowlist)
}

function cleanNode(node: Element, allowlist: Allowlist) {
  if (DROP_TAGS.has(node.tagName)) {
    node.remove()
    return
  }

  cleanChildren(node, allowlist)

  if (!allowlist.tags.has(node.tagName)) {
    // Keep the words, lose the tag.
    node.replaceWith(...Array.from(node.childNodes))
    return
  }

  const allowed = allowlist.attrs[node.tagName] ?? new Set<string>()
  for (const attr of Array.from(node.attributes)) {
    if (!allowed.has(attr.name.toLowerCase())) node.removeAttribute(attr.name)
  }

  if (node.tagName === 'A') {
    const href = node.getAttribute('href') ?? ''
    if (!SAFE_HREF.test(href)) node.removeAttribute('href')
    else {
      node.setAttribute('rel', 'noopener noreferrer')
      node.setAttribute('target', '_blank')
    }
  }

  if (node.tagName === 'IMG' && !SAFE_SRC.test(node.getAttribute('src') ?? '')) node.remove()
}

/**
 * Reduces rich text to the small tag set notes are allowed to carry. Use on
 * anything that reaches `dangerouslySetInnerHTML` — note bodies are written by
 * users and stored verbatim, so the API hands them back exactly as posted.
 */
export function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  cleanChildren(doc.body, NOTE_ALLOWLIST)
  return doc.body.innerHTML
}

/**
 * Like {@link sanitizeHtml}, but keeps the headings, images and code that
 * published content such as a release note is written with.
 */
export function sanitizeRichHtml(html: string): string {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  cleanChildren(doc.body, RICH_ALLOWLIST)
  return doc.body.innerHTML
}

/** The note's text with no markup — for previews, titles and empty checks. */
export function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  return (doc.body.textContent ?? '').trim()
}
